import { UploadDiagnostics } from "./UploadDiagnostics";

/**
 * Central Map<uploadId, UploadDiagnostics>. Consumers call getOrCreate() to
 * grab (or lazily construct) a diagnostics object for a given uploadId — the
 * bridge layer uses this to route uploader signals into diagnostics without
 * caring about instance lifecycle.
 *
 * Eviction is explicit: services call registry.dispose(uploadId) when an
 * upload reaches a terminal state or is cancelled. Without explicit disposal
 * the object lives forever — that is intentional so the admin UI can still
 * inspect diagnostics for a completed upload before the page navigates away.
 */
class UploadDiagnosticsRegistryImpl {
  constructor() {
    /** @type {Map<string, UploadDiagnostics>} */
    this._byId = new Map();
    /** @type {Set<(diagnostics: UploadDiagnostics) => void>} */
    this._createListeners = new Set();
    /** @type {Set<(uploadId: string) => void>} */
    this._disposeListeners = new Set();
  }

  /**
   * @param {string} uploadId
   * @param {Record<string, any>} [initialContext]
   * @returns {UploadDiagnostics}
   */
  getOrCreate(uploadId, initialContext) {
    if (!uploadId) {
      throw new Error("registry.getOrCreate requires an uploadId");
    }
    let existing = this._byId.get(uploadId);
    if (existing) {
      if (initialContext) existing.setContext(initialContext);
      return existing;
    }
    const created = new UploadDiagnostics(uploadId, initialContext);
    this._byId.set(uploadId, created);
    for (const listener of this._createListeners) {
      try {
        listener(created);
      } catch (_error) {
        /* observers must not break writers */
      }
    }
    return created;
  }

  /** @returns {UploadDiagnostics | null} */
  get(uploadId) {
    return this._byId.get(uploadId) || null;
  }

  has(uploadId) {
    return this._byId.has(uploadId);
  }

  /** All diagnostics currently held (used by StuckDetector sweep). */
  all() {
    return Array.from(this._byId.values());
  }

  /** Dispose a specific upload's diagnostics + optional stored timeline. */
  dispose(uploadId, options) {
    const existing = this._byId.get(uploadId);
    if (!existing) return;
    existing.dispose(options);
    this._byId.delete(uploadId);
    for (const listener of this._disposeListeners) {
      try {
        listener(uploadId);
      } catch (_error) {
        /* swallow */
      }
    }
  }

  /** Test hook — drops every entry. */
  clear() {
    for (const diag of this._byId.values()) {
      try {
        diag.dispose();
      } catch (_error) {}
    }
    this._byId.clear();
  }

  onCreate(listener) {
    if (typeof listener !== "function") return () => {};
    this._createListeners.add(listener);
    return () => this._createListeners.delete(listener);
  }

  onDispose(listener) {
    if (typeof listener !== "function") return () => {};
    this._disposeListeners.add(listener);
    return () => this._disposeListeners.delete(listener);
  }
}

const registry = new UploadDiagnosticsRegistryImpl();

/**
 * Module-level singleton. Everyone in the app shares this — the registry is
 * the "process-wide" observer of every upload.
 */
export const uploadDiagnosticsRegistry = registry;

// Test-only reset. Not part of the public surface.
export const __resetRegistryForTests = () => registry.clear();
